import { useEffect } from 'react';

interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  url: string;
  type?: 'website' | 'article' | 'webpage';
  image?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  breadcrumbs?: { name: string; url: string }[];
  faq?: { question: string; answer: string }[];
}

export function useSEO(config: SEOConfig) {
  useEffect(() => {
    document.title = config.title;

    const updateMeta = (name: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        if (isProperty) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    updateMeta('description', config.description);
    if (config.keywords) updateMeta('keywords', config.keywords);

    updateMeta('og:title', config.title, true);
    updateMeta('og:description', config.description, true);
    updateMeta('og:type', config.type || 'website', true);
    updateMeta('og:url', config.url, true);
    if (config.image) updateMeta('og:image', config.image, true);

    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', config.title);
    updateMeta('twitter:description', config.description);
    if (config.image) updateMeta('twitter:image', config.image);

    // JSON-LD scripts
    const schemas: Record<string, unknown>[] = [];

    // Organization
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '51爆料',
      description: '吃瓜爆料黑料第一站，网红翻车、擦边反差、明星八卦24小时实时更新',
      url: 'https://www.51baoliao.com',
      logo: 'https://www.51baoliao.com/logo.png',
      sameAs: [
        'https://weibo.com/51baoliao',
        'https://www.douyin.com/user/51baoliao',
      ],
    });

    // WebSite
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '51爆料',
      url: 'https://www.51baoliao.com',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.51baoliao.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    });

    // WebPage
    schemas.push({
      '@context': 'https://schema.org',
      '@type': config.type === 'article' ? 'Article' : 'WebPage',
      name: config.title,
      description: config.description,
      url: config.url,
      image: config.image,
      ...(config.publishedTime && { datePublished: config.publishedTime }),
      ...(config.modifiedTime && { dateModified: config.modifiedTime }),
      ...(config.author && { author: { '@type': 'Organization', name: config.author } }),
      ...(config.section && { articleSection: config.section }),
    });

    // BreadcrumbList
    if (config.breadcrumbs && config.breadcrumbs.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: config.breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url,
        })),
      });
    }

    // FAQPage
    if (config.faq && config.faq.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: config.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      });
    }

    // Remove old JSON-LD scripts with data-seo attr
    document.querySelectorAll('script[data-seo]').forEach((el) => el.remove());

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo]').forEach((el) => el.remove());
    };
  }, [config]);
}